import { REMOVE_WORKSPACE_LOGO } from "@components/common/sphere-dropzone/dropzone-elements";
import { useIsUserDragging } from "@hooks/use-is-user-dragging";
import { Box } from "@mui-wrapper";
import { BrowserUtils } from "@stellar/utils";
import { FontWeights } from "@flat-ui";
import { useCallback, useMemo, useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";
/** Handles dragging effect and file drop */
export function DropHandler({
  children,
  canBeDropped,
  onDrop,
  onClick,
  isLoading,
  shouldAllowMultiUpload
}) {
  const isAppDragging = useIsUserDragging();

  /** Check if the current element is receiving dragging events */
  const [isDragging, setIsDragging] = useState(false);
  const [itemsCount, setItemsCount] = useState(0);
  const onDragEnterAndOver = useCallback(event => {
    event.preventDefault();
    const items = event.dataTransfer.items.length;
    setItemsCount(items);

    /**
     * Sets the appropriate drag-and-drop effect and allowed actions based on specified conditions.
     * This logic is used to allow users to add file in Safari where the dataTransfer event may not
     * work as expected.
     */
    if (canBeDropped && (items === 1 || shouldAllowMultiUpload || BrowserUtils.isSafari())) {
      event.dataTransfer.dropEffect = "copy";
      event.dataTransfer.effectAllowed = "all";
    } else {
      event.dataTransfer.dropEffect = "none";
      event.dataTransfer.effectAllowed = "none";
    }
    event.stopPropagation();
    setIsDragging(true);
  }, [canBeDropped, shouldAllowMultiUpload]);
  const onDragLeave = useCallback(event_0 => {
    event_0.preventDefault();
    event_0.stopPropagation();
    setIsDragging(false);
    setItemsCount(0);
  }, []);
  const onFileDrop = useCallback(event_1 => {
    onDragLeave(event_1);
    onDrop(event_1);
  }, [onDrop, onDragLeave]);
  const borderStyle = useMemo(() => {
    if (!shouldAllowMultiUpload && itemsCount > 1) {
      return `2px solid ${PALETTE_VARS.error.light}`;
    }
    if (isDragging) {
      return `2px solid ${PALETTE_VARS.primary.main}`;
    }
    if (isAppDragging) {
      return `2px dashed ${PALETTE_VARS.primary.light}`;
    }
    return `2px dashed ${PALETTE_VARS.divider}`;
  }, [shouldAllowMultiUpload, itemsCount, isDragging, isAppDragging]);
  return <Box component="div" onDrop={onFileDrop} onDragEnter={onDragEnterAndOver} onDragOver={onDragEnterAndOver} onDragLeave={onDragLeave} onClick={onClick} sx={{
    width: "100%",
    height: "250px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-evenly",
    fontSize: "16px",
    fontWeight: FontWeights.SemiBold,
    border: borderStyle,
    textAlign: "center",
    position: "relative",
    flexDirection: "column",
    cursor: isLoading ? "default" : "pointer",
    borderRadius: "4px",
    ...(!isLoading && {
      "&:hover": {
        border: `2px solid ${PALETTE_VARS.primary.main}`,
        [`& .${REMOVE_WORKSPACE_LOGO}`]: {
          // Show the remove workspace logo button (which is hidden by default), when hovering over the dropzone
          visibility: "visible"
        }
      }
    })
  }}>
      {children}
    </Box>;
}